import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import setUpAxios from "../setUpAxios";
import LoadingScreen from "./reusable/loadingScreen";
import { FaUserPlus, FaSearch, FaUser, FaChevronRight, FaPhoneAlt, FaArrowRight } from "react-icons/fa";
import PageHeader from "./reusable/PageHeader";
import { useLoginContext } from "../Contexts/LoginContext";

function CustomerTable() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const { admin } = useLoginContext();
  setUpAxios();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCustomers = async () => {
      setLoading(true);
      setError(null);
      try {
        const { data } = await api.get(`/users`);
        if (Array.isArray(data)) {
          setCustomers(data);
        } else {
          setError("Unexpected response format.");
        }
      } catch (err) {
        setError("Failed to load customers.");
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, []);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return customers;
    const lowerQuery = searchQuery.toLowerCase();
    return customers.filter((c) =>
      c.name?.toLowerCase().includes(lowerQuery) ||
      c.mobile_number?.includes(lowerQuery) ||
      c.username?.toLowerCase().includes(lowerQuery)
    );
  }, [customers, searchQuery]);

  // Helper to get initials
  const getInitials = (name) => {
    return name ? name.charAt(0).toUpperCase() : <FaUser />;
  };

  // Helper to handle call click without opening profile
  const handleCall = (e, mobile) => {
    e.stopPropagation(); // Stop the card from clicking
    window.location.href = `tel:${mobile}`;
  };

  if (loading) return <LoadingScreen message="Loading customers..." />;
  if (error) return <div className="p-10 text-center text-red-500 font-medium">{error}</div>;
  if (!admin) return <div className="p-10 text-center text-gray-500">Access Denied.</div>;

  return (
    <div className="min-h-screen w-full bg-gray-50 pb-24 md:pb-10">
      
      {/* --- Sticky Header --- */}
      <div className="sticky top-0 z-20 bg-white/95 backdrop-blur-md border-b border-gray-200 px-4 py-3 shadow-sm">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-3">
            <PageHeader title="Customers" />
            
            {/* Desktop Add Button */}
            <button
              className="hidden md:flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-full transition-all shadow-md active:scale-95"
              onClick={() => navigate("/addCustomer")}
            >
              <FaUserPlus /> <span>Add Customer</span>
            </button>

            {/* Mobile Add Icon (Visible in header too for clarity) */}
            <button 
                onClick={() => navigate("/addCustomer")}
                className="md:hidden p-2 text-blue-600 bg-blue-50 rounded-full"
            >
                <FaUserPlus />
            </button>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search name or number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-11 pr-4 py-3 bg-gray-100 border-none rounded-xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-base"
            />
          </div>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 mt-4">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3 ml-1">
          {filtered.length} Customer{filtered.length !== 1 && 's'} Found
        </p>

        {filtered.length > 0 ? (
          <div className="space-y-3">
            {filtered.map((c) => (
              <div
                key={c.id}
                onClick={() => navigate(`/user/${c.id}`, { state: c })}
                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 active:scale-[0.98] transition-transform duration-100 cursor-pointer relative overflow-hidden group"
              >
                {/* Visual Indicator on Left */}
                <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-blue-500"></div>

                <div className="flex items-start gap-4 pl-2">
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    {c.profile_img ? (
                      <img
                        src={c.profile_img}
                        alt={c.name}
                        className="w-14 h-14 rounded-full object-cover ring-2 ring-gray-100"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-100 to-blue-50 text-blue-600 flex items-center justify-center font-bold text-xl shadow-inner">
                        {getInitials(c.name)}
                      </div>
                    )}
                  </div>

                  {/* Info Section */}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <h3 className="text-gray-900 font-bold text-lg truncate capitalize">
                        {c.name}
                      </h3>
                      {/* Balance Badge */}
                      <span className="bg-green-50 text-green-700 px-2.5 py-1 rounded-lg text-xs font-bold border border-green-100">
                        ₹{Number(c.total).toLocaleString('en-IN')}
                      </span>
                    </div>

                    <p className="text-gray-500 text-sm mt-0.5 flex items-center gap-1">
                      {c.mobile_number || "No number"}
                    </p>

                    {/* Action Buttons Row */}
                    <div className="flex items-center gap-3 mt-3">
     
                        
                        {/* Explicit View Profile Button */}
                        <button className="flex-1 flex items-center justify-center gap-2 bg-blue-50 text-blue-700 py-2 rounded-lg text-sm font-semibold group-hover:bg-blue-100 transition-colors">
                            View Profile <FaArrowRight size={12} />
                        </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Empty State */
          <div className="flex flex-col items-center justify-center py-16 text-center">
             <div className="bg-white p-4 rounded-full shadow-sm mb-4">
                <FaSearch className="text-4xl text-gray-300" />
             </div>
             <h3 className="text-gray-900 font-bold text-lg">No customers found</h3>
             <p className="text-gray-500 text-sm mt-1 max-w-[200px]">
                Try searching for a different name or number.
             </p>
          </div>
        )}
      </main>

      {/* Floating Action Button (Mobile Only) */}
      <button
        onClick={() => navigate("/addCustomer")}
        className="md:hidden fixed bottom-6 right-6 w-14 h-14 bg-blue-600 text-white rounded-full shadow-2xl shadow-blue-300 flex items-center justify-center text-xl active:scale-90 transition-transform z-30"
        aria-label="Add Customer"
      >
        <FaUserPlus />
      </button>
    </div>
  );
}

export default CustomerTable;